/* global theme */

/**
 * Backs `sections/film-processing-form.liquid`.
 *
 * State is keyed by `_eps_*` block id, and derived from the DOM on every connect
 * rather than stored: Symmetry rebuilds the section on `shopify:section:load`, so
 * merchants can add, remove and reorder option blocks without stale keys.
 *
 * Pricing: a process variant bills per roll of that process, while scan, print,
 * negative and add-on variants bill at the total roll count across all processes.
 * Each block carries its variant price in minor units on `data-price`.
 */
if (!customElements.get('film-processing-form')) {
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // International-friendly: optional leading +, then 7-15 digits (the E.164
  // range), after stripping common separators.
  const PHONE_REGEX = /^\+?[0-9]{7,15}$/;

  const REQUIRED_REFS = [
    'totalDisplay',
    'submitBtn',
    'firstNameInput',
    'lastNameInput',
    'emailInput',
    'phoneInput',
    'instructionsInput'
  ];

  /** Ordered: a failed submit focuses the first entry whose `isValid` fails. */
  const CONTACT_FIELDS = [
    {
      inputRef: 'firstNameInput',
      errorRef: 'firstNameError',
      required: 'firstNameRequired',
      isValid: (value) => value.length > 0
    },
    {
      inputRef: 'lastNameInput',
      errorRef: 'lastNameError',
      required: 'lastNameRequired',
      isValid: (value) => value.length > 0
    },
    {
      inputRef: 'emailInput',
      errorRef: 'emailError',
      required: 'emailRequired',
      invalid: 'emailInvalid',
      isValid: (value) => EMAIL_REGEX.test(value)
    },
    {
      inputRef: 'phoneInput',
      errorRef: 'phoneError',
      required: 'phoneRequired',
      invalid: 'phoneInvalid',
      // Strip everything but digits and +, so any separator style is tolerated.
      isValid: (value) => PHONE_REGEX.test(value.replace(/[^\d+]/g, ''))
    }
  ];

  /** The single-select groups. Add-ons toggle independently, so they sit outside. */
  const GROUPS = {
    scan: { selector: '.eps-scan-option', dataKey: 'scanKey', stateKey: 'scanKey' },
    print: { selector: '.eps-print-option', dataKey: 'printKey', stateKey: 'printKey' },
    neg: { selector: '.eps-neg-option', dataKey: 'negKey', stateKey: 'negKey' }
  };

  /**
   * What a print option or add-on can require, keyed by the name its notes carry
   * on `data-requirement`. An option is available while every `data-requires-*`
   * it has equals `current` of the selection snapshot (`requirementContext`), and
   * `satisfy` selects what meets it. A paused print meets no requirement, so an
   * add-on on it pauses too.
   *
   * Ordered: `satisfyRequirements` meets print first, so an option's own scanner
   * wins over the scanner its print pulls in.
   */
  const REQUIREMENTS = {
    print: {
      dataKey: 'requiresPrint',
      current: (ctx) => ctx.printName,
      // Prefers a print with that name that already works on the current scanner.
      satisfy: (form, value) => {
        const matches = form.groupOptions('print').filter((el) => el.dataset.printName === value);
        const print = matches.find((el) => form.isAvailable(el)) || matches[0];
        if (!print) return;
        form.satisfyRequirements(print);
        form.selectOption('print', print.dataset.printKey);
      }
    },
    scanner: {
      dataKey: 'requiresScanner',
      current: (ctx) => ctx.scanner,
      satisfy: (form, value) => {
        const scan = form.groupOptions('scan').find((el) => el.dataset.scanner === value);
        if (scan) form.selectOption('scan', scan.dataset.scanKey);
      }
    }
  };

  const ADDON_SELECTOR = '.eps-addon-card[data-addon-key]';
  const PROCESS_SELECTOR = '.eps-process-card[data-process-key]';

  /**
   * Currencies whose minor unit is not 1/100. Liquid prices are in minor units,
   * so the divisor has to come from the currency itself — deriving it from the
   * shop's "HTML without currency" setting would be wrong for any merchant who
   * has edited that format. Mirrors Shopify's own table.
   */
  const CURRENCY_DECIMALS = {
    BHD: 3,
    BIF: 0,
    BYR: 0,
    CLF: 4,
    CLP: 0,
    DJF: 0,
    GNF: 0,
    IQD: 3,
    ISK: 0,
    JOD: 3,
    JPY: 0,
    KMF: 0,
    KRW: 0,
    KWD: 3,
    LYD: 3,
    MRO: 5,
    OMR: 3,
    PYG: 0,
    RWF: 0,
    TND: 3,
    UGX: 0,
    UYI: 0,
    UYW: 4,
    VND: 0,
    VUV: 0,
    XAF: 0,
    XAG: 0,
    XAU: 0,
    XBA: 0,
    XBB: 0,
    XBC: 0,
    XBD: 0,
    XDR: 0,
    XOF: 0,
    XPD: 0,
    XPF: 0,
    XPT: 0,
    XSU: 0,
    XTS: 0,
    XUA: 0
  };

  const DEFAULT_CURRENCY_DECIMALS = 2;

  const MONEY_RULES = {
    amount: { thousands: ',', decimal: '.' },
    amount_no_decimals: { thousands: ',', decimal: '.', precision: 0 },
    amount_with_comma_separator: { thousands: '.', decimal: ',' },
    amount_no_decimals_with_comma_separator: { thousands: '.', decimal: ',', precision: 0 },
    amount_with_space_separator: { thousands: ' ', decimal: ',' },
    amount_no_decimals_with_space_separator: { thousands: ' ', decimal: ',', precision: 0 },
    amount_with_period_and_space_separator: { thousands: ' ', decimal: '.' },
    amount_with_apostrophe_separator: { thousands: "'", decimal: '.' }
  };

  /**
   * Stands in for Shopify's `money` filter: Symmetry ships no money formatter and
   * the running total has to update without a round trip. `amount` is in minor
   * units, `currency` the presentment currency's ISO code.
   */
  function formatMoney(amount, format, currency) {
    const code = String(currency || '').toUpperCase();
    const nativePrecision = code in CURRENCY_DECIMALS ? CURRENCY_DECIMALS[code] : DEFAULT_CURRENCY_DECIMALS;
    const divisor = 10 ** nativePrecision;

    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
      if (placeholder === 'currency') return currency || '';

      const rule = MONEY_RULES[placeholder];
      if (!rule) return match;

      const precision = rule.precision === undefined ? nativePrecision : rule.precision;
      const parts = (amount / divisor).toFixed(precision).split('.');
      const grouped = parts[0].replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + rule.thousands);

      if (precision <= 0) return grouped;
      return grouped + rule.decimal + (parts[1] || '').padEnd(precision, '0');
    });
  }

  /** Keyed by `data-eps-action`, which the block Liquid stamps onto each control. */
  const CLICK_ACTIONS = {
    qty: (form, el) => {
      const card = el.closest('[data-process-key]');
      if (card) form.changeQty(card.dataset.processKey, Number(el.dataset.epsDelta));
    },
    'qty-reset': (form, el) => {
      const card = el.closest('[data-process-key]');
      if (!card) return;
      form.resetQty(card.dataset.processKey);
      // The reset button hides itself, so hand focus to the stepper rather than
      // dropping it to <body>.
      const increment = card.querySelector('[data-eps-delta="1"]');
      if (increment) increment.focus();
    },
    'select-scan': (form, el) => form.selectOption('scan', el.dataset.scanKey),
    'select-print': (form, el) => form.selectOption('print', el.dataset.printKey),
    'select-neg': (form, el) => form.selectOption('neg', el.dataset.negKey),
    'toggle-addon': (form, el) => form.toggleAddon(el.dataset.addonKey),
    submit: (form) => form.addToCart()
  };

  class FilmProcessingForm extends HTMLElement {
    connectedCallback() {
      // Re-attached on every connect, so the theme editor keeps working if the
      // element is moved in the DOM rather than re-rendered.
      this.bindBlockSelect();

      if (this.initialised) return;
      this.initialised = true;

      this.refs = {};
      this.querySelectorAll('[data-ref]').forEach((el) => {
        this.refs[el.dataset.ref] = el;
      });

      this.strings = this.parseStrings();
      if (!this.checkRequiredRefs()) return;

      this.state = { quantities: {}, scanKey: null, printKey: null, negKey: null, addons: {} };

      // Validation errors stay hidden until the customer has tried to submit, so
      // the form isn't pre-littered with errors on load.
      this.submitAttempted = false;

      // Tracks whether the shared error region currently holds the "add a roll"
      // message rather than a cart failure, so live re-validation only clears the
      // message it owns.
      this.rollsErrorShown = false;

      this.uniquifyErrorIds();
      this.bindEvents();
      this.initFromDom();
      this.sync();
      this.setAttribute('loaded', '');
    }

    disconnectedCallback() {
      if (!this.blockSelectHandler) return;
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      this.blockSelectHandler = null;
    }

    /** Falls back to `{}` so a missing attribute blanks the messages rather than throwing. */
    parseStrings() {
      try {
        return JSON.parse(this.dataset.strings || '{}');
      } catch (err) {
        return {};
      }
    }

    /**
     * A merchant who empties a container in the theme editor should get a disabled
     * form, not a broken page — so this disables rather than throws.
     */
    checkRequiredRefs() {
      const missing = REQUIRED_REFS.filter((name) => !this.refs[name]);
      if (missing.length === 0) return true;

      if (this.refs.submitBtn) this.refs.submitBtn.disabled = true;
      return false;
    }

    /**
     * Nothing but `aria-describedby` reads these ids, so rewriting them at runtime is
     * safe and makes two instances of the section on one page collision-proof.
     */
    uniquifyErrorIds() {
      const uid = Math.random().toString(36).slice(2, 8);
      this.querySelectorAll('[data-ref$="Error"]').forEach((el) => {
        el.id = `eps-err-${uid}-${el.dataset.ref}`;
      });
    }

    /**
     * Separate from the delegated listeners: those live on this element and are
     * collected with it, but a `document` listener has to be detached by hand.
     */
    bindBlockSelect() {
      if (this.blockSelectHandler) return;
      this.blockSelectHandler = this.handleBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
    }

    bindEvents() {
      theme.addDelegateEventListener(this, 'click', '[data-eps-action]', (evt, el) => {
        const action = CLICK_ACTIONS[el.dataset.epsAction];
        if (!action) return;
        evt.preventDefault();
        action(this, el);
      });

      theme.addDelegateEventListener(this, 'input', '[data-eps-action="contact-input"]', () => {
        this.refreshValidation();
      });

      theme.addDelegateEventListener(this, 'keydown', '[role="radio"]', (evt, el) => {
        this.handleGroupKeydown(evt, el);
      });
    }

    /** Keeps the theme editor's selected block and the form's selected option in step. */
    handleBlockSelect(evt) {
      const el = evt.target;
      if (!this.contains(el)) return;

      // The event fires on the block's outer element. For process cards that is the
      // card itself; for the other options it is the .eps-option wrapper around
      // the button that holds the state. Only a wrapper's own button counts, so
      // selecting a whole form group doesn't select the first option inside it.
      const find = (selector) =>
        el.closest(selector) || (el.matches('.eps-option') ? el.querySelector(`:scope > ${selector}`) : null);

      // An unavailable option would otherwise ignore the selection, so first select
      // what it requires.
      const groupNames = Object.keys(GROUPS);
      for (let i = 0; i < groupNames.length; i += 1) {
        const group = GROUPS[groupNames[i]];
        const option = find(group.selector);
        if (option) {
          this.satisfyRequirements(option);
          this.selectOption(groupNames[i], option.dataset[group.dataKey]);
          return;
        }
      }

      const addon = find(ADDON_SELECTOR);
      if (addon) {
        this.satisfyRequirements(addon);
        if (!this.state.addons[addon.dataset.addonKey]) this.toggleAddon(addon.dataset.addonKey);
        return;
      }

      const process = el.closest(PROCESS_SELECTOR);
      if (process && !this.state.quantities[process.dataset.processKey]) {
        this.changeQty(process.dataset.processKey, 1);
      }
    }

    processCards() {
      return Array.from(this.querySelectorAll(PROCESS_SELECTOR));
    }

    addonCards() {
      return Array.from(this.querySelectorAll(ADDON_SELECTOR));
    }

    groupOptions(name) {
      return Array.from(this.querySelectorAll(GROUPS[name].selector));
    }

    selectedOption(name) {
      const group = GROUPS[name];
      const key = this.state[group.stateKey];
      if (!key) return null;
      return this.groupOptions(name).find((el) => el.dataset[group.dataKey] === key) || null;
    }

    /**
     * Groups start on the block flagged "Selected by default", else the first one,
     * skipping options unavailable on the starting selection: with none available
     * the group starts empty rather than paused on a choice the customer never
     * made. Scan is set before print, so a print's scanner is already known.
     * Process cards flagged "Selected by default" start at one roll; the rest at zero.
     */
    initFromDom() {
      this.state.quantities = {};
      this.processCards().forEach((card) => {
        const key = card.dataset.processKey;
        if (key) this.state.quantities[key] = Number(card.dataset.default === 'true');
      });

      Object.keys(GROUPS).forEach((name) => {
        const group = GROUPS[name];
        const options = this.groupOptions(name);
        const candidates = [options.find((el) => el.dataset.default === 'true')].concat(options);
        const preferred = candidates.find((el) => el && this.isAvailable(el));
        this.state[group.stateKey] = preferred ? preferred.dataset[group.dataKey] : null;
      });

      this.state.addons = {};
      this.addonCards().forEach((card) => {
        const key = card.dataset.addonKey;
        if (key) this.state.addons[key] = false;
      });
    }

    sync() {
      this.syncProcessCards();
      this.syncSelections();
      this.recalc();
      this.refreshValidation();
    }

    syncProcessCards() {
      this.processCards().forEach((card) => {
        const key = card.dataset.processKey;
        if (!key) return;
        const qty = this.state.quantities[key] || 0;

        card.classList.toggle('active', qty > 0);

        const display = card.querySelector('[data-qty-display]');
        if (display) display.textContent = String(qty);

        const decrement = card.querySelector('[data-qty-decrement]');
        if (decrement) decrement.disabled = qty === 0;

        // Hidden with visibility rather than `hidden`, so its space stays reserved
        // and the card doesn't change height when the quantity leaves zero.
        const reset = card.querySelector('[data-qty-reset]');
        if (reset) reset.classList.toggle('is-hidden', qty === 0);
      });
    }

    syncGroup(name) {
      const group = GROUPS[name];
      const activeKey = this.state[group.stateKey];
      const options = this.groupOptions(name);
      const hasSelection = options.some((el) => el.dataset[group.dataKey] === activeKey);

      options.forEach((el, i) => {
        const isSelected = el.dataset[group.dataKey] === activeKey;
        el.classList.toggle('selected', isSelected);
        if (el.getAttribute('role') !== 'radio') return;

        el.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        // A radiogroup is a single tab stop: only the selected option is
        // reachable by Tab, and the arrow keys move within the group.
        const isTabStop = hasSelection ? isSelected : i === 0;
        el.tabIndex = isTabStop ? 0 : -1;
      });
    }

    /**
     * The `radiogroup` role the markup declares promises this. Selection follows
     * focus, and wraps. Focus still reaches an unavailable option, so its note is
     * read out, but selection stays where it was.
     */
    handleGroupKeydown(evt, el) {
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[evt.key];
      if (!step) return;

      const name = Object.keys(GROUPS).find((key) => el.matches(GROUPS[key].selector));
      if (!name) return;

      const group = GROUPS[name];
      const options = this.groupOptions(name);
      const current = options.indexOf(el);
      if (current === -1) return;

      evt.preventDefault();
      const next = options[(current + step + options.length) % options.length];
      if (this.isAvailable(next)) this.selectOption(name, next.dataset[group.dataKey]);
      next.focus();
    }

    /**
     * Brings every option's selected and available state up to date. Any selection
     * can change what another option requires, so this always re-syncs them all.
     */
    syncSelections() {
      Object.keys(GROUPS).forEach((name) => this.syncGroup(name));

      const ctx = this.requirementContext();

      // A paused print stays selected, like a paused add-on stays on. Unlike the
      // add-on, clicking it does nothing, so it stays disabled.
      Object.keys(GROUPS).forEach((name) => {
        const selected = this.selectedOption(name);
        this.groupOptions(name).forEach((el) => {
          this.syncAvailability(el, { chosen: el === selected, clickable: false }, ctx);
        });
      });

      // A paused add-on — chosen, but with a requirement unmet — keeps its toggle on
      // and says it is paused, so it coming back on when the requirement is met
      // again is no surprise. Clicking it turns it off.
      this.addonCards().forEach((card) => {
        const key = card.dataset.addonKey;
        if (!key) return;
        const chosen = Boolean(this.state.addons[key]);
        card.classList.toggle('selected', chosen);
        card.setAttribute('aria-pressed', chosen ? 'true' : 'false');
        this.syncAvailability(card, { chosen, clickable: chosen }, ctx);
      });
    }

    /**
     * Shows one note per unmet requirement: "paused" when the customer has chosen
     * the option, "requires" when they haven't.
     *
     * When the only thing blocking an add-on's print is that the print, though
     * selected, is itself paused, the add-on repeats the print's own note (e.g.
     * "Requires Frontier scans") instead of asking for a print already chosen.
     */
    syncAvailability(el, { chosen, clickable }, ctx) {
      const unmet = this.unmetRequirements(el, ctx);
      const variant = chosen ? 'paused' : 'requires';

      // aria-disabled rather than disabled, so the option stays focusable and the
      // note saying why it is unavailable can still be reached.
      el.classList.toggle('is-unavailable', unmet.length > 0);
      if (unmet.length === 0 || clickable) el.removeAttribute('aria-disabled');
      else el.setAttribute('aria-disabled', 'true');

      const blockingPrint =
        unmet.indexOf('print') !== -1 &&
        ctx.pausedPrint &&
        ctx.pausedPrint.dataset.printName === el.dataset.requiresPrint
          ? ctx.pausedPrint
          : null;
      const own = blockingPrint ? unmet.filter((name) => name !== 'print') : unmet;

      el.querySelectorAll('[data-unavailable-note]').forEach((note) => {
        note.hidden = note.dataset.unavailableNote !== variant || own.indexOf(note.dataset.requirement) === -1;
      });

      const inherited = el.querySelector('[data-inherited-note]');
      if (!inherited) return;
      let text = '';
      if (blockingPrint) {
        const printUnmet = this.unmetRequirements(blockingPrint, ctx);
        text = Array.from(blockingPrint.querySelectorAll(`[data-unavailable-note="${variant}"]`))
          .filter((note) => printUnmet.indexOf(note.dataset.requirement) !== -1)
          .map((note) => note.textContent.trim())
          .join(' ');
      }
      inherited.textContent = text;
      inherited.hidden = !text;
    }

    /**
     * A snapshot of what the current selection offers REQUIREMENTS, taken once per
     * pass so each option doesn't re-query the groups: the selected scan's scanner,
     * and the selected print's name, or `pausedPrint` when that print is paused.
     * Values are normalized by the block Liquid; '' for none.
     */
    requirementContext() {
      const scan = this.selectedOption('scan');
      const ctx = { scanner: (scan && scan.dataset.scanner) || '', printName: '', pausedPrint: null };

      // Prints never require a print, so checking one before printName is set is safe.
      const print = this.selectedOption('print');
      if (print && this.isAvailable(print, ctx)) ctx.printName = print.dataset.printName || '';
      else if (print) ctx.pausedPrint = print;

      return ctx;
    }

    /** The names of the REQUIREMENTS an option declares but the current selection doesn't meet. */
    unmetRequirements(el, ctx = this.requirementContext()) {
      return Object.keys(REQUIREMENTS).filter((name) => {
        const requirement = REQUIREMENTS[name];
        const required = el.dataset[requirement.dataKey];
        return Boolean(required) && required !== requirement.current(ctx);
      });
    }

    isAvailable(el, ctx) {
      return this.unmetRequirements(el, ctx).length === 0;
    }

    /**
     * Whether an add-on is billed. `state.addons` holds the customer's choice, which
     * survives the add-on being unavailable, so meeting its requirements again
     * restores it.
     */
    isAddonOn(card, ctx) {
      return Boolean(this.state.addons[card.dataset.addonKey]) && this.isAvailable(card, ctx);
    }

    /** A group's selected option, unless it is paused: that is the one billed and ordered. */
    activeOption(name, ctx) {
      const selected = this.selectedOption(name);
      return selected && this.isAvailable(selected, ctx) ? selected : null;
    }

    /**
     * Makes an option available by selecting what it requires. Requirements already
     * met are left alone, so the customer's other choices only change when they must.
     */
    satisfyRequirements(el) {
      Object.keys(REQUIREMENTS).forEach((name) => {
        // Re-checked each time, since meeting one requirement can meet or break another.
        if (this.unmetRequirements(el).indexOf(name) === -1) return;
        const requirement = REQUIREMENTS[name];
        requirement.satisfy(this, el.dataset[requirement.dataKey]);
      });
    }

    changeQty(key, delta) {
      if (!(key in this.state.quantities) || !Number.isFinite(delta)) return;
      this.setQty(key, this.state.quantities[key] + delta);
    }

    resetQty(key) {
      if (!(key in this.state.quantities)) return;
      this.setQty(key, 0);
    }

    setQty(key, qty) {
      this.state.quantities[key] = Math.max(0, qty);
      this.syncProcessCards();
      this.recalc();
      this.refreshValidation();
    }

    selectOption(name, key) {
      const group = GROUPS[name];
      const option = key && this.groupOptions(name).find((el) => el.dataset[group.dataKey] === key);
      if (!option || !this.isAvailable(option)) return;
      this.state[group.stateKey] = key;
      this.syncSelections();
      this.recalc();
    }

    toggleAddon(key) {
      if (!(key in this.state.addons)) return;
      // An unavailable add-on can't be turned on, but a paused one can always be
      // turned off, so the customer can drop it without changing their other choices first.
      const card = this.addonCards().find((el) => el.dataset.addonKey === key);
      if (!this.state.addons[key] && card && !this.isAvailable(card)) return;
      this.state.addons[key] = !this.state.addons[key];
      this.syncSelections();
      this.recalc();
    }

    /** The multiplier for every scan, print, negative and add-on line item. */
    totalRolls() {
      return Object.keys(this.state.quantities).reduce((sum, key) => sum + this.state.quantities[key], 0);
    }

    /** Missing or non-numeric `data-price` reads as 0 — that is how free options are priced. */
    priceOf(el) {
      const raw = Number(el.dataset.price);
      return Number.isFinite(raw) ? raw : 0;
    }

    recalc() {
      const totalRolls = this.totalRolls();
      let total = 0;

      this.processCards().forEach((card) => {
        const key = card.dataset.processKey;
        if (key) total += this.priceOf(card) * (this.state.quantities[key] || 0);
      });

      const ctx = this.requirementContext();
      Object.keys(GROUPS).forEach((name) => {
        const active = this.activeOption(name, ctx);
        if (active) total += this.priceOf(active) * totalRolls;
      });

      this.addonCards().forEach((card) => {
        const key = card.dataset.addonKey;
        if (key && this.isAddonOn(card, ctx)) total += this.priceOf(card) * totalRolls;
      });

      this.refs.totalDisplay.textContent = formatMoney(total, this.dataset.moneyFormat, this.dataset.currency);
    }

    /** Returns invalid fields in CONTACT_FIELDS order, which is also the focus order. */
    computeContactErrors() {
      const errors = [];
      CONTACT_FIELDS.forEach((field) => {
        const input = this.refs[field.inputRef];
        const value = input.value.trim();
        const messageKey = value.length === 0 ? field.required : field.invalid;

        if (value.length === 0 || !field.isValid(value)) {
          errors.push({ field, input, message: this.strings[messageKey] || '' });
        }
      });
      return errors;
    }

    renderContactErrors(errors) {
      const invalid = errors || this.computeContactErrors();
      CONTACT_FIELDS.forEach((field) => {
        const match = invalid.find((entry) => entry.field === field);
        if (match) this.showFieldError(field, match.message);
        else this.clearFieldError(field);
      });
    }

    showFieldError(field, message) {
      const input = this.refs[field.inputRef];
      const errorEl = this.refs[field.errorRef];

      if (errorEl) {
        const span = errorEl.querySelector('span');
        if (span) span.textContent = message;
        errorEl.hidden = false;
        input.setAttribute('aria-describedby', errorEl.id);
      }
      input.setAttribute('aria-invalid', 'true');
    }

    clearFieldError(field) {
      const input = this.refs[field.inputRef];
      const errorEl = this.refs[field.errorRef];

      if (errorEl) {
        errorEl.hidden = true;
        const span = errorEl.querySelector('span');
        if (span) span.textContent = '';
      }
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }

    /** No-ops until the first submit, so the form isn't pre-littered with errors on load. */
    refreshValidation() {
      if (!this.submitAttempted) return;
      this.renderContactErrors();
      if (this.totalRolls() === 0) this.showRollsError();
      else this.clearRollsError();
    }

    showRollsError() {
      this.showError(this.strings.noRolls);
      this.rollsErrorShown = true;
    }

    /** Leaves a cart failure the customer is still reading in place. */
    clearRollsError() {
      if (this.rollsErrorShown) this.clearError();
    }

    /**
     * Customer detail rides as line-item properties on the process items only, so the
     * lab reads the whole order off one line instead of five copies.
     */
    buildItems() {
      const totalRolls = this.totalRolls();
      const firstName = this.refs.firstNameInput.value.trim();
      const lastName = this.refs.lastNameInput.value.trim();
      const instructions = this.refs.instructionsInput.value.trim();

      // A paused print is left out, as if none were selected.
      const ctx = this.requirementContext();
      const selected = {};
      Object.keys(GROUPS).forEach((name) => {
        selected[name] = this.activeOption(name, ctx);
      });

      // These keys and values appear in the merchant's order admin, so they stay
      // English rather than following the storefront locale: translating them
      // would make order data locale-dependent and break anything parsing it.
      const properties = {
        'Customer name': `${firstName} ${lastName}`,
        'Customer email': this.refs.emailInput.value.trim(),
        'Customer phone': this.refs.phoneInput.value.trim(),
        'Scan type': (selected.scan && selected.scan.dataset.label) || 'None',
        'Print type': (selected.print && selected.print.dataset.label) || 'None',
        Negatives: (selected.neg && selected.neg.dataset.label) || 'None',
        Instructions: instructions || 'None'
      };

      // Add-on labels are merchant-authored and become property keys, so one named
      // "Instructions" or "Scan type" would otherwise overwrite that field and the
      // lab would silently lose the customer's answer.
      const reserved = Object.keys(properties);
      this.addonCards().forEach((card) => {
        const { label, addonKey } = card.dataset;
        if (!label || !addonKey) return;
        const key = reserved.indexOf(label) === -1 ? label : `Add-on: ${label}`;
        properties[key] = this.isAddonOn(card, ctx) ? 'Yes' : 'No';
      });

      const items = [];

      this.processCards().forEach((card) => {
        const { processKey, variantId } = card.dataset;
        const qty = this.state.quantities[processKey] || 0;
        if (qty > 0 && variantId) {
          items.push({ id: Number(variantId), quantity: qty, properties });
        }
      });

      const pushPerRoll = (el) => {
        if (el && el.dataset.variantId && totalRolls > 0) {
          items.push({ id: Number(el.dataset.variantId), quantity: totalRolls });
        }
      };

      Object.keys(GROUPS).forEach((name) => pushPerRoll(selected[name]));
      this.addonCards().forEach((card) => {
        if (this.isAddonOn(card, ctx)) pushPerRoll(card);
      });

      return items;
    }

    /**
     * The button stays clickable while the form is invalid: submitting reveals the
     * field errors and focuses the first, which beats a disabled button that never
     * says why.
     */
    async addToCart() {
      this.submitAttempted = true;

      const errors = this.computeContactErrors();
      this.renderContactErrors(errors);
      const noRolls = this.totalRolls() === 0;
      if (noRolls) this.showRollsError();
      else this.clearRollsError();

      if (errors.length > 0 || noRolls) {
        const messages = errors.map((entry) => entry.message);
        if (noRolls) messages.push(this.strings.noRolls || '');
        // Focus only lands on the first invalid field, so announce them all.
        this.announce(messages.join('. '));

        if (errors.length > 0) {
          const input = errors[0].input;
          theme.scrollToRevealElement(input);
          input.focus({ preventScroll: true });
        }
        return;
      }

      const items = this.buildItems();
      if (items.length === 0) {
        this.showError(this.strings.notConfigured);
        return;
      }

      const btn = this.refs.submitBtn;
      const originalLabel = btn.textContent;
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.textContent = this.strings.adding || '';
      this.clearError();

      const sectionEls = ['page-header', 'cart-drawer']
        .map((selector) => document.querySelector(selector))
        .filter((el) => el);

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: sectionEls.map((el) => el.dataset.sectionId).join(','),
            sections_url: window.location.pathname
          })
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
          // Flagged so the catch can tell a cart error we can show the customer
          // from a transport failure, whose message is an untranslated browser
          // string such as "Failed to fetch".
          const cartError = new Error((data && (data.description || data.message)) || '');
          cartError.isCartError = true;
          throw cartError;
        }

        this.handleAddSuccess(data, sectionEls, btn);
      } catch (err) {
        this.showError((err.isCartError && err.message) || this.strings.cartError);
        this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: err.message } }));
        // The cart may have changed despite the error, so refresh regardless.
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.textContent = originalLabel;
      }
    }

    handleAddSuccess(data, sectionEls, btn) {
      const sections = data && data.sections;
      const haveAllSections =
        sections && sectionEls.length > 0 && sectionEls.every((el) => sections[el.dataset.sectionId]);

      if (haveAllSections) {
        sectionEls.forEach((el) => el.updateFromCartChange(sections[el.dataset.sectionId]));
      } else {
        // page-header and cart-form both re-fetch themselves on this, so it is a
        // complete fallback when the response carries no sections.
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      }

      // A multi-item add returns { items: [...] }, not a bare line item.
      const firstItem = data && data.items && data.items[0];
      this.dispatchEvent(
        new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: firstItem && firstItem.variant_id, items: data && data.items }
        })
      );

      // When a confirmation modal is wired up it becomes the sole confirmation,
      // replacing the theme's usual after-add-to-cart behaviour.
      const modalId = this.dataset.upsellModalId;
      const modal = modalId ? document.getElementById(modalId) : null;
      if (modal && typeof modal.open === 'function') {
        this.reset();
        modal.open(btn);
        return;
      }

      const mode = theme.settings.afterAddToCart;

      // 'drawer' is selectable even when the cart type is 'page', in which case
      // there is no drawer to open.
      if (mode === 'drawer' && document.querySelector('.js-cart-drawer')) {
        this.reset();
        document.dispatchEvent(
          new CustomEvent('dispatch:cart-drawer:open', {
            bubbles: true,
            cancelable: false,
            detail: { opener: btn }
          })
        );
      } else if (mode === 'notification' && document.getElementById('AddedNotification')) {
        this.reset();
        this.showAddedNotification(firstItem);
      } else {
        // Full-page cart. The form is left behind, so there is nothing to reset.
        window.location.href = theme.routes.cart;
      }
    }

    showAddedNotification(firstItem) {
      const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
      notification.dataset.productTitle = (firstItem && firstItem.product_title) || '';
      (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
    }

    reset() {
      this.submitAttempted = false;
      this.state = { quantities: {}, scanKey: null, printKey: null, negKey: null, addons: {} };

      ['firstNameInput', 'lastNameInput', 'emailInput', 'phoneInput', 'instructionsInput'].forEach((ref) => {
        this.refs[ref].value = '';
      });

      CONTACT_FIELDS.forEach((field) => this.clearFieldError(field));
      this.clearError();
      this.announce('');
      this.initFromDom();
      this.sync();
    }

    showError(message) {
      const region = this.refs.errorRegion;
      if (!region) return;
      region.textContent = message || '';
      region.hidden = false;
    }

    clearError() {
      this.rollsErrorShown = false;
      const region = this.refs.errorRegion;
      if (!region) return;
      region.textContent = '';
      region.hidden = true;
    }

    /** Focus lands on one field, so the live region has to carry the rest. */
    announce(message) {
      if (this.refs.contactStatus) this.refs.contactStatus.textContent = message;
    }
  }

  customElements.define('film-processing-form', FilmProcessingForm);
}
